/**
 * Stage 6–7 class lists (super-advanced harmony & mastery).
 */

import type { TutorClass, TutorStage } from "./types.js";
import { normalizeTutorClass } from "./kindAssessment.js";

function cls(partial: TutorClass): TutorClass {
  return normalizeTutorClass(partial);
}

export const STAGE_6: TutorClass[] = [
  cls({
    id: "class-6-1",
    stage: 6,
    code: "6.1",
    title: "Seventh Chords Lab",
    kind: "chord",
    defaultMinutes: 40,
    minMinutes: 20,
    maxMinutes: 60,
    description: "Dominant 7, maj7, min7 block vocabulary.",
    segments: [
      { pieceId: "chord-c7-dom", baseLoops: 4, label: "C7" },
      { pieceId: "chord-cmaj7", baseLoops: 4, label: "Cmaj7" },
      { pieceId: "chord-amin7", baseLoops: 4, label: "Am7" },
      {
        pieceId: "chord-c7-dom",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 76,
        label: "C7 locked",
      },
    ],
  }),
  cls({
    id: "class-6-2",
    stage: 6,
    code: "6.2",
    title: "Dim / Aug / Sus Colors",
    kind: "chord",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Diminished, augmented, sus2/sus4 shapes.",
    segments: [
      { pieceId: "chord-bdim", baseLoops: 4, label: "B dim" },
      { pieceId: "chord-caug", baseLoops: 4, label: "C aug" },
      { pieceId: "chord-csus2", baseLoops: 3, label: "Sus2" },
      { pieceId: "chord-csus4", baseLoops: 3, label: "Sus4→maj" },
    ],
  }),
  cls({
    id: "class-6-3",
    stage: 6,
    code: "6.3",
    title: "ii–V–I Cadence",
    kind: "chord",
    defaultMinutes: 40,
    minMinutes: 20,
    maxMinutes: 60,
    description: "The jazz/pop core progression in C.",
    segments: [
      { pieceId: "prog-ii-v-i-c", baseLoops: 6, label: "ii–V–I" },
      {
        pieceId: "prog-ii-v-i-c",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 80,
        label: "ii–V–I locked",
      },
    ],
  }),
  cls({
    id: "class-6-4",
    stage: 6,
    code: "6.4",
    title: "Inversions Beyond C",
    kind: "chord",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "1st inversions in G / F / D major.",
    segments: [
      { pieceId: "chord-g-inv1", baseLoops: 4, label: "G inv1" },
      { pieceId: "chord-f-inv1", baseLoops: 4, label: "F inv1" },
      { pieceId: "chord-d-inv1", baseLoops: 4, label: "D inv1" },
    ],
  }),
  cls({
    id: "class-6-5",
    stage: 6,
    code: "6.5",
    title: "Circle of Fifths March",
    kind: "chord",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Walk major triads around the circle excerpt.",
    segments: [
      { pieceId: "circle-fifths-triads", baseLoops: 5, label: "Circle triads" },
      {
        pieceId: "circle-fifths-triads",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 84,
        label: "Circle locked",
      },
    ],
  }),
  cls({
    id: "class-6-6",
    stage: 6,
    code: "6.6",
    title: "LH Progressions + Shells",
    kind: "chord",
    defaultMinutes: 40,
    minMinutes: 20,
    maxMinutes: 60,
    description: "Practice LH I–V–vi–IV roots, then RH shell voicings — separately, then lock timing.",
    segments: [
      { pieceId: "lh-prog-i-v-vi-iv", baseLoops: 5, label: "LH pop prog" },
      { pieceId: "voicing-shell-c7", baseLoops: 5, label: "C7 shells" },
      {
        pieceId: "lh-prog-i-v-vi-iv",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 88,
        label: "LH locked",
      },
    ],
  }),
];

export const STAGE_7: TutorClass[] = [
  cls({
    id: "class-7-1",
    stage: 7,
    code: "7.1",
    title: "New Major Keys (Bb / E / A)",
    kind: "scale",
    defaultMinutes: 45,
    minMinutes: 22,
    maxMinutes: 70,
    description: "Super-advanced key fluency — Bb, E, A major scales.",
    segments: [
      { pieceId: "scale-bb-major-rh", baseLoops: 4, label: "Bb major" },
      { pieceId: "scale-e-major-rh", baseLoops: 4, label: "E major" },
      { pieceId: "scale-a-major-rh", baseLoops: 4, label: "A major" },
      {
        pieceId: "scale-e-major-rh",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 84,
        label: "E locked",
      },
    ],
  }),
  cls({
    id: "class-7-2",
    stage: 7,
    code: "7.2",
    title: "Mini Etude Phrasing",
    kind: "speed",
    defaultMinutes: 40,
    minMinutes: 20,
    maxMinutes: 60,
    description: "Longer ascending-break etude patterns.",
    segments: [
      { pieceId: "etude-c-ascending-break", baseLoops: 5, label: "Etude" },
      {
        pieceId: "etude-c-ascending-break",
        baseLoops: 3,
        metronomeLocked: true,
        bpm: 96,
        label: "Etude locked",
      },
    ],
  }),
  cls({
    id: "class-7-3",
    stage: 7,
    code: "7.3",
    title: "Held-Bass Independence",
    kind: "polyphony",
    defaultMinutes: 40,
    minMinutes: 20,
    maxMinutes: 60,
    description: "True both-hands: LH bass changes while RH moves.",
    segments: [
      { pieceId: "both-held-bass-independence", baseLoops: 6, label: "Held bass" },
      {
        pieceId: "both-held-bass-independence",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 76,
        label: "Held bass locked",
      },
    ],
  }),
  cls({
    id: "class-7-4",
    stage: 7,
    code: "7.4",
    title: "Ear Call & Response",
    kind: "ear",
    assessment: "ear",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Listen, then echo step patterns from memory.",
    segments: [
      { pieceId: "ear-echo-steps-c", baseLoops: 6, label: "Echo steps" },
      {
        pieceId: "ear-echo-steps-c",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 72,
        label: "Echo locked",
      },
    ],
  }),
  cls({
    id: "class-7-5",
    stage: 7,
    code: "7.5",
    title: "Timing-Perfect Quarters",
    kind: "rhythm",
    defaultMinutes: 35,
    minMinutes: 18,
    maxMinutes: 55,
    description: "Metronome-locked quarters — sync accuracy focus.",
    segments: [
      {
        pieceId: "rhythm-perfect-quarters-locked",
        baseLoops: 6,
        metronomeLocked: true,
        bpm: 90,
        label: "Quarters @90",
      },
      {
        pieceId: "rhythm-perfect-quarters-locked",
        baseLoops: 4,
        metronomeLocked: true,
        bpm: 110,
        label: "Quarters @110",
      },
    ],
  }),
  cls({
    id: "class-7-6",
    stage: 7,
    code: "7.6",
    title: "Dynamics & Pedal Mindset",
    kind: "pedal",
    defaultMinutes: 30,
    minMinutes: 15,
    maxMinutes: 50,
    description: "Soft/loud velocity awareness + real sustain-pedal change cues.",
    assessment: "pedal",
    skills: ["dynamics", "sustain pedal", "clean changes"],
    segments: [
      { pieceId: "dynamics-soft-loud-c", baseLoops: 5, label: "Soft↔loud", focus: "dynamics" },
      { pieceId: "pedal-sustain-cue-c", baseLoops: 4, label: "Pedal tones", focus: "pedal" },
    ],
  }),
];

export const ADVANCED_STAGES: TutorStage[] = [
  {
    id: 6,
    title: "Extended Harmony Lab",
    blurb: "Super-advanced — 7ths, colors, ii–V–I, shells, circle (~4 hrs).",
    examId: "exam-stage-6",
    classes: STAGE_6,
  },
  {
    id: 7,
    title: "Keys, Etudes & Artistry",
    blurb: "Master — new majors, etudes, independence, ear, timing (~4 hrs).",
    examId: "exam-stage-7",
    classes: STAGE_7,
  },
];
